/**
 * Offers the store in the shopper's own language.
 *
 * The whole decision happens here rather than in Liquid because Shopify caches
 * page HTML and that cache ignores `Accept-Language`: a choice made on the
 * server would be baked into the cached page and served to the next visitor in
 * a different language. The markup is identical for everyone and hidden; this
 * script is what reveals it.
 *
 * It offers, it never redirects. Shopify's guidance is explicit that an
 * automatic redirect is the wrong pattern, and one reason is measurable: search
 * engine crawlers arrive with no cookies and no language header, so a redirect
 * indexes the store in whatever language the crawler's location implies.
 */
(() => {
  const STORE_KEY = 'yarn-social:locale-suggest-dismissed';

  /** The primary subtag, lowercased: `tr-TR` and `tr` both give `tr`. */
  const base = (tag) => String(tag || '').toLowerCase().split('-')[0];

  /** localStorage throws in private mode and when site data is blocked. */
  const remember = () => {
    try {
      localStorage.setItem(STORE_KEY, '1');
    } catch (error) {
      /* A shopper who cannot be remembered simply sees the offer again. */
    }
  };

  const dismissed = () => {
    try {
      return localStorage.getItem(STORE_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  class LocalizationSuggest extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // A shopper who has already answered is not asked again, and one who picks
      // a language anywhere in the theme has answered by doing so.
      this.watchOtherPickers();
      if (dismissed()) return;

      const match = this.preferred();
      if (!match) return;

      this.fill(match);
      this.hidden = false;
    }

    /** The languages the store publishes, as the Liquid printed them. */
    languages() {
      const node = this.querySelector('[data-lsg-languages]');
      if (!node) return [];
      try {
        const parsed = JSON.parse(node.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    /**
     * The first of the shopper's languages, in their own order of preference,
     * that the store publishes and that is not what they are already reading.
     *
     * `navigator.languages` is already sorted by the quality weights the browser
     * would send, so the order is taken from it rather than recomputed.
     */
    preferred() {
      const current = base(this.dataset.current);
      const available = this.languages();
      const wanted = navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language];

      for (const tag of wanted) {
        const want = base(tag);
        if (!want) continue;
        // The shopper's best language is the one they already have: nothing to
        // offer, and nothing further down their list is worth offering either.
        if (want === current) return null;
        const hit = available.find((entry) => base(entry.iso) === want);
        if (hit) return hit;
      }
      return null;
    }

    fill(language) {
      const text = this.querySelector('[data-lsg-text]');
      const code = this.querySelector('[data-lsg-code]');
      const accept = this.querySelector('[data-lsg-switch]');
      const close = this.querySelector('[data-lsg-dismiss]');
      if (!text || !code || !accept || !close) return;

      text.textContent = language.suggest;
      code.value = language.iso;
      accept.textContent = language.switch;
      close.textContent = language.dismiss;

      // The bar and its controls are in the offered language, not the page's, so
      // they are marked as such — otherwise a screen reader reads Turkish with
      // an English voice.
      this.lang = language.iso;

      accept.addEventListener('click', remember);
      close.addEventListener('click', () => {
        remember();
        this.hidden = true;
      });
    }

    /**
     * Choosing a language in the header or the footer answers this question too.
     * The listener is on the document because those forms belong to sections
     * that may be re-rendered by the theme editor.
     */
    watchOtherPickers() {
      document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form === this.querySelector('form')) return;
        if (form.querySelector('[name="locale_code"]')) remember();
      });
    }
  }

  if (!customElements.get('localization-suggest')) {
    customElements.define('localization-suggest', LocalizationSuggest);
  }
})();
